import { Fragment, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { FundPriceChart } from './FundPriceChart';
import {
  ApiError,
  confirmVehicleImport,
  createHolding,
  createVehicle,
  deleteHolding,
  deleteVehicle,
  listFunds,
  listFundPrices,
  listHoldings,
  listVehicles,
  previewVehicleImport,
  updateHolding,
  updateVehicle,
  type EnrichedHolding,
  type Fund,
  type FundPriceSnapshot,
  type ImportPreviewResponse,
  type ImportPreviewRow,
  type ImportResponse,
  type Vehicle,
} from '../api';

interface InvestmentForm {
  fund_id: string;
  vehicle_id: string;
  quantity: string;
  book_cost_pounds: string;
  provider: string;
  platform: string;
  opened_at: string;
}

const emptyInvestmentForm: InvestmentForm = {
  fund_id: '',
  vehicle_id: '',
  quantity: '',
  book_cost_pounds: '',
  provider: '',
  platform: '',
  opened_at: todayISO(),
};

interface VehicleForm {
  name: string;
  owner: string;
  kind: string;
  cash_pounds: string;
  platform_fee_pct: string;
  platform_fee_flat_pounds: string;
  advisor_fee_pct: string;
}

const emptyVehicleForm: VehicleForm = {
  name: '',
  owner: '',
  kind: '',
  cash_pounds: '',
  platform_fee_pct: '',
  platform_fee_flat_pounds: '',
  advisor_fee_pct: '',
};

const KIND_SUGGESTIONS = [
  'SIPP',
  'ISA',
  'JISA',
  'LISA',
  'GIA',
  'Pension',
  'Crypto Wallet',
  'Other',
];
const UNASSIGNED_KEY = '__unassigned__';

// Performance buckets for the row-level filter. Ranges are half-open on the
// upper bound so a fund returning exactly 9% is "well", exactly 6% is
// "average", etc — matches the labels the buttons advertise.
type PerformanceFilter = 'well' | 'average' | 'poor' | 'disastrous' | null;

const PERF_FILTERS: {
  key: Exclude<PerformanceFilter, null>;
  label: string;
  colour: string;
}[] = [
  { key: 'well', label: 'Well (≥9%)', colour: '#0a7d32' },
  { key: 'average', label: 'Average (6–9%)', colour: '#15803d' },
  { key: 'poor', label: 'Poor (1–6%)', colour: '#b45309' },
  { key: 'disastrous', label: 'Disastrous (<1%)', colour: '#b00' },
];

function holdingMatchesFilter(h: EnrichedHolding, f: PerformanceFilter): boolean {
  if (f == null) return true;
  // Only investments with a computed annualised growth pass any filter.
  // Unrated rows are hidden from filter views so the result reflects what
  // the buckets actually cover.
  if (h.growth_pct_per_year == null) return false;
  const g = h.growth_pct_per_year;
  switch (f) {
    case 'well':
      return g >= 9;
    case 'average':
      return g >= 6 && g < 9;
    case 'poor':
      return g >= 1 && g < 6;
    case 'disastrous':
      return g < 1;
  }
}

// Value-weighted average annualised growth across rows that have one.
// Returns null when no row qualifies (no NAV-at-open or zero MV).
function weightedAvgGrowth(rows: EnrichedHolding[]): number | null {
  let totalMv = 0;
  let weighted = 0;
  for (const h of rows) {
    if (h.growth_pct_per_year == null || h.market_value_pence == null) continue;
    totalMv += h.market_value_pence;
    weighted += h.market_value_pence * h.growth_pct_per_year;
  }
  if (totalMv === 0) return null;
  return weighted / totalMv;
}

export function InvestmentsSection({
  accountId,
  perfFilter,
  onPerfFilterChange,
  onPortfolioLoad,
}: {
  accountId: string;
  // Controlled when provided so the dashboard can drive the filter from
  // alert deep-links (?perf=disastrous etc). When undefined the section
  // owns its own filter state.
  perfFilter?: PerformanceFilter;
  onPerfFilterChange?: (next: PerformanceFilter) => void;
  // Surface the loaded data back to the parent so the dashboard can
  // build its KPI strip / alerts off the same fetch instead of
  // duplicating the network call.
  onPortfolioLoad?: (data: { holdings: EnrichedHolding[]; vehicles: Vehicle[] }) => void;
}) {
  const [holdings, setHoldings] = useState<EnrichedHolding[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [funds, setFunds] = useState<Fund[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showAddInvestment, setShowAddInvestment] = useState(false);
  const [investmentForm, setInvestmentForm] = useState<InvestmentForm>(emptyInvestmentForm);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [vehicleForm, setVehicleForm] = useState<VehicleForm>(emptyVehicleForm);
  const [submitting, setSubmitting] = useState(false);

  const [editingHoldingId, setEditingHoldingId] = useState<string | null>(null);
  const [editHoldingForm, setEditHoldingForm] = useState<InvestmentForm>(emptyInvestmentForm);

  const [editingVehicleId, setEditingVehicleId] = useState<string | null>(null);
  const [editVehicleForm, setEditVehicleForm] = useState<VehicleForm>(emptyVehicleForm);

  const [importVehicleId, setImportVehicleId] = useState<string | null>(null);
  // expandedHoldingId is the row whose price-chart drawer is open. Only
  // one at a time — clicking another row swaps which drawer is shown.
  const [expandedHoldingId, setExpandedHoldingId] = useState<string | null>(null);
  // Vehicles start collapsed by default (seeded once on first load). The
  // per-vehicle summary stays visible either way so the header is always
  // informative. Users can toggle individual chevrons or use the
  // expand/collapse-all control above the table.
  const [collapsedVehicleIds, setCollapsedVehicleIds] = useState<Set<string>>(new Set());
  const seededCollapsedRef = useRef(false);
  const [unassignedCollapsed, setUnassignedCollapsed] = useState(true);
  const [internalPerfFilter, setInternalPerfFilter] = useState<PerformanceFilter>(null);
  const activePerfFilter = perfFilter !== undefined ? perfFilter : internalPerfFilter;
  const setPerfFilter = (next: PerformanceFilter) => {
    if (onPerfFilterChange) onPerfFilterChange(next);
    else setInternalPerfFilter(next);
  };

  async function load() {
    setError(null);
    try {
      const [hRes, fRes, vRes] = await Promise.all([
        listHoldings(accountId),
        listFunds(accountId),
        listVehicles(accountId),
      ]);
      setHoldings(hRes.holdings);
      setFunds(fRes.funds);
      setVehicles(vRes.vehicles);
      onPortfolioLoad?.({ holdings: hRes.holdings, vehicles: vRes.vehicles });
    } catch (err) {
      setError(asMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!accountId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  useEffect(() => {
    if (seededCollapsedRef.current) return;
    if (vehicles.length === 0) return;
    setCollapsedVehicleIds(new Set(vehicles.map((v) => v.id)));
    seededCollapsedRef.current = true;
  }, [vehicles]);

  async function onCreateInvestment(e: FormEvent) {
    e.preventDefault();
    const qty = Number(investmentForm.quantity);
    const cost = Number(investmentForm.book_cost_pounds);
    if (!investmentForm.fund_id || !investmentForm.vehicle_id) {
      setError('Fund and vehicle are required');
      return;
    }
    if (!Number.isFinite(qty) || qty <= 0) {
      setError('Quantity must be a positive number');
      return;
    }
    if (!Number.isFinite(cost) || cost < 0) {
      setError('Book cost must be a non-negative number');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createHolding(accountId, {
        fund_id: investmentForm.fund_id,
        vehicle_id: investmentForm.vehicle_id,
        quantity: qty,
        book_cost_pence: Math.round(cost * 100),
        provider: investmentForm.provider.trim() || undefined,
        platform: investmentForm.platform.trim() || undefined,
        opened_at: investmentForm.opened_at.trim() || undefined,
      });
      setInvestmentForm({ ...emptyInvestmentForm, opened_at: todayISO() });
      setShowAddInvestment(false);
      await load();
    } catch (err) {
      setError(asMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onCreateVehicle(e: FormEvent) {
    e.preventDefault();
    const cash = vehicleForm.cash_pounds.trim() === '' ? 0 : Number(vehicleForm.cash_pounds);
    if (!vehicleForm.name.trim() || !vehicleForm.owner.trim()) {
      setError('Name and owner are required');
      return;
    }
    if (!Number.isFinite(cash) || cash < 0) {
      setError('Cash must be a non-negative number');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createVehicle(accountId, {
        name: vehicleForm.name.trim(),
        owner: vehicleForm.owner.trim(),
        kind: vehicleForm.kind.trim() || undefined,
        cash_pence: Math.round(cash * 100),
      });
      setVehicleForm(emptyVehicleForm);
      setShowAddVehicle(false);
      await load();
    } catch (err) {
      setError(asMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  function startEditHolding(h: EnrichedHolding) {
    setEditingHoldingId(h.id);
    setEditHoldingForm({
      fund_id: h.fund_id,
      vehicle_id: h.vehicle_id ?? '',
      quantity: String(h.quantity),
      book_cost_pounds: (h.book_cost_pence / 100).toFixed(2),
      provider: h.provider ?? '',
      platform: h.platform ?? '',
      opened_at: h.opened_at ?? '',
    });
  }

  async function onSaveEditHolding(e: FormEvent, holdingId: string) {
    e.preventDefault();
    const qty = Number(editHoldingForm.quantity);
    const cost = Number(editHoldingForm.book_cost_pounds);
    if (!editHoldingForm.vehicle_id) {
      setError('Vehicle is required');
      return;
    }
    if (!Number.isFinite(qty) || qty <= 0) {
      setError('Quantity must be a positive number');
      return;
    }
    if (!Number.isFinite(cost) || cost < 0) {
      setError('Book cost must be a non-negative number');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await updateHolding(accountId, holdingId, {
        vehicle_id: editHoldingForm.vehicle_id,
        quantity: qty,
        book_cost_pence: Math.round(cost * 100),
        provider: editHoldingForm.provider.trim() || undefined,
        platform: editHoldingForm.platform.trim() || undefined,
        opened_at: editHoldingForm.opened_at.trim() || undefined,
      });
      setEditingHoldingId(null);
      await load();
    } catch (err) {
      setError(asMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onDeleteHolding(holdingId: string) {
    if (!window.confirm('Delete this investment?')) return;
    setError(null);
    try {
      await deleteHolding(accountId, holdingId);
      await load();
    } catch (err) {
      setError(asMessage(err));
    }
  }

  function startEditVehicle(v: Vehicle) {
    setEditingVehicleId(v.id);
    setEditVehicleForm({
      name: v.name,
      owner: v.owner ?? '',
      kind: v.kind ?? '',
      cash_pounds: (v.cash_pence / 100).toFixed(2),
      platform_fee_pct:
        v.platform_fee_pct_per_year != null && v.platform_fee_pct_per_year > 0
          ? String(v.platform_fee_pct_per_year)
          : '',
      platform_fee_flat_pounds:
        v.platform_fee_flat_pence != null && v.platform_fee_flat_pence > 0
          ? (v.platform_fee_flat_pence / 100).toFixed(2)
          : '',
      advisor_fee_pct:
        v.advisor_fee_pct_per_year != null && v.advisor_fee_pct_per_year > 0
          ? String(v.advisor_fee_pct_per_year)
          : '',
    });
  }

  async function onSaveEditVehicle(e: FormEvent, vehicleId: string) {
    e.preventDefault();
    const cash =
      editVehicleForm.cash_pounds.trim() === '' ? 0 : Number(editVehicleForm.cash_pounds);
    if (!editVehicleForm.name.trim() || !editVehicleForm.owner.trim()) {
      setError('Name and owner are required');
      return;
    }
    if (!Number.isFinite(cash) || cash < 0) {
      setError('Cash must be a non-negative number');
      return;
    }
    const feePct =
      editVehicleForm.platform_fee_pct.trim() === '' ? 0 : Number(editVehicleForm.platform_fee_pct);
    const feeFlat =
      editVehicleForm.platform_fee_flat_pounds.trim() === ''
        ? 0
        : Number(editVehicleForm.platform_fee_flat_pounds);
    const advisorPct =
      editVehicleForm.advisor_fee_pct.trim() === '' ? 0 : Number(editVehicleForm.advisor_fee_pct);
    if (
      !Number.isFinite(feePct) ||
      feePct < 0 ||
      !Number.isFinite(feeFlat) ||
      feeFlat < 0 ||
      !Number.isFinite(advisorPct) ||
      advisorPct < 0
    ) {
      setError('Fees must be non-negative numbers');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await updateVehicle(accountId, vehicleId, {
        name: editVehicleForm.name.trim(),
        owner: editVehicleForm.owner.trim(),
        kind: editVehicleForm.kind.trim() || undefined,
        cash_pence: Math.round(cash * 100),
        platform_fee_pct_per_year: feePct,
        platform_fee_flat_pence: Math.round(feeFlat * 100),
        advisor_fee_pct_per_year: advisorPct,
      });
      setEditingVehicleId(null);
      await load();
    } catch (err) {
      setError(asMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onDeleteVehicle(v: Vehicle) {
    const stillHas = holdings.some((h) => h.vehicle_id === v.id);
    if (stillHas) {
      window.alert(`${v.name} still has investments inside it. Reassign or delete those first.`);
      return;
    }
    if (!window.confirm(`Delete ${v.name}?`)) return;
    setError(null);
    try {
      await deleteVehicle(accountId, v.id);
      await load();
    } catch (err) {
      setError(asMessage(err));
    }
  }

  // Recalc book cost for every holding in a vehicle that has both an
  // opened_at date and a known NAV-at-open. Sets book_cost = qty ×
  // NAV_at_open so gain/loss reflects price movement since the buy-in
  // date — fix for legacy paste-imports where book == MV.
  async function onRecalcVehicleBookCosts(v: Vehicle) {
    const targets = holdings.filter(
      (h) => h.vehicle_id === v.id && h.opened_at && h.nav_at_open_pence != null
    );
    if (targets.length === 0) {
      window.alert(
        'No holdings in this vehicle have an open date + historical NAV to recalc from.'
      );
      return;
    }
    const msg =
      `Recalculate book cost for ${targets.length} ${targets.length === 1 ? 'holding' : 'holdings'} ` +
      `in ${v.name}? Each book cost will be set to quantity × NAV on its open date.`;
    if (!window.confirm(msg)) return;
    setError(null);
    setSubmitting(true);
    try {
      for (const h of targets) {
        const navAtOpen = h.nav_at_open_pence!;
        const newBookPence = Math.round(h.quantity * navAtOpen);
        await updateHolding(accountId, h.id, {
          vehicle_id: h.vehicle_id ?? '',
          quantity: h.quantity,
          book_cost_pence: newBookPence,
          provider: h.provider,
          platform: h.platform,
          opened_at: h.opened_at,
        });
      }
      await load();
    } catch (err) {
      setError(asMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <p>Loading…</p>;

  // Group investments by vehicle_id; null/empty → unassigned bucket.
  // Apply the active performance filter as we group so downstream summary
  // calcs (count / weighted growth) operate on the visible rows only.
  const groups = new Map<string, EnrichedHolding[]>();
  for (const h of holdings) {
    if (!holdingMatchesFilter(h, activePerfFilter)) continue;
    const key = h.vehicle_id || UNASSIGNED_KEY;
    const list = groups.get(key) ?? [];
    list.push(h);
    groups.set(key, list);
  }
  // Unfiltered group, used for grand totals (totals always reflect the
  // whole portfolio so the bottom row stays honest under filters).
  const allGroups = new Map<string, EnrichedHolding[]>();
  for (const h of holdings) {
    const key = h.vehicle_id || UNASSIGNED_KEY;
    const list = allGroups.get(key) ?? [];
    list.push(h);
    allGroups.set(key, list);
  }

  // Render order: every vehicle (even empty ones), then unassigned at the
  // bottom if it has any rows. Empty vehicles still render so the user can
  // see + edit cash + delete them. Under an active filter, vehicles whose
  // entire investment list filtered out are hidden — keeping them would
  // just be empty headers.
  const sortedVehicles = [...vehicles]
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((v) => activePerfFilter == null || (groups.get(v.id)?.length ?? 0) > 0);
  const unassigned = groups.get(UNASSIGNED_KEY) ?? [];

  // Grand totals reflect the *whole* portfolio (use allGroups), independent
  // of the row filter, so the footer is always the truth — the filter just
  // changes what's *shown*, not what you own.
  let totalBook = 0;
  let totalMarket = 0;
  let totalCost = 0;
  let allValued = true;
  for (const v of vehicles) {
    totalBook += v.cash_pence;
    totalMarket += v.cash_pence;
    let groupMarket = v.cash_pence;
    for (const h of allGroups.get(v.id) ?? []) {
      totalBook += h.book_cost_pence;
      if (h.market_value_pence != null) {
        totalMarket += h.market_value_pence;
        groupMarket += h.market_value_pence;
        totalCost += (h.market_value_pence * (h.fund_ocf_pct ?? 0)) / 100;
      } else allValued = false;
    }
    totalCost += (groupMarket * (v.platform_fee_pct_per_year ?? 0)) / 100;
    totalCost += v.platform_fee_flat_pence ?? 0;
    totalCost += (groupMarket * (v.advisor_fee_pct_per_year ?? 0)) / 100;
  }
  for (const h of allGroups.get(UNASSIGNED_KEY) ?? []) {
    totalBook += h.book_cost_pence;
    if (h.market_value_pence != null) {
      totalMarket += h.market_value_pence;
      totalCost += (h.market_value_pence * (h.fund_ocf_pct ?? 0)) / 100;
    } else allValued = false;
  }
  totalCost = Math.round(totalCost);
  const totalCostPct = totalMarket > 0 ? (totalCost / totalMarket) * 100 : 0;
  const totalGain = totalMarket - totalBook;
  const totalGainPct = allValued && totalBook > 0 ? (totalGain / totalBook) * 100 : null;
  const hasAnything = sortedVehicles.length > 0 || holdings.length > 0;

  const importVehicle = sortedVehicles.find((v) => v.id === importVehicleId) ?? null;

  return (
    <div>
      {error && <p style={{ color: '#b00' }}>{error}</p>}

      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
        <button onClick={() => setShowAddVehicle((v) => !v)}>
          {showAddVehicle ? 'Cancel' : 'New vehicle'}
        </button>
        {funds.length > 0 && sortedVehicles.length > 0 && (
          <button onClick={() => setShowAddInvestment((v) => !v)}>
            {showAddInvestment ? 'Cancel' : 'New investment'}
          </button>
        )}
        {sortedVehicles.length > 0 &&
          (() => {
            const anyExpanded =
              sortedVehicles.some((v) => !collapsedVehicleIds.has(v.id)) ||
              (unassigned.length > 0 && !unassignedCollapsed);
            return (
              <button
                onClick={() => {
                  if (anyExpanded) {
                    setCollapsedVehicleIds(new Set(sortedVehicles.map((v) => v.id)));
                    setUnassignedCollapsed(true);
                  } else {
                    setCollapsedVehicleIds(new Set());
                    setUnassignedCollapsed(false);
                  }
                }}
              >
                {anyExpanded ? 'Collapse all' : 'Expand all'}
              </button>
            );
          })()}
      </div>

      {showAddVehicle && (
        <VehicleFormUI
          form={vehicleForm}
          setForm={setVehicleForm}
          submitting={submitting}
          onSubmit={onCreateVehicle}
          submitLabel="Save vehicle"
        />
      )}

      {showAddInvestment && funds.length > 0 && sortedVehicles.length > 0 && (
        <InvestmentFormUI
          form={investmentForm}
          setForm={setInvestmentForm}
          funds={funds}
          vehicles={sortedVehicles}
          submitting={submitting}
          onSubmit={onCreateInvestment}
          submitLabel="Save investment"
        />
      )}

      {funds.length === 0 && (
        <p style={{ color: '#666' }}>
          Add a fund first —{' '}
          <Link to={`/accounts/${encodeURIComponent(accountId)}/funds`}>
            upload a fund report PDF
          </Link>{' '}
          to enable investments.
        </p>
      )}

      {importVehicle && (
        <ImportFromPaste
          accountId={accountId}
          vehicle={importVehicle}
          onClose={() => setImportVehicleId(null)}
          onImported={async () => {
            setImportVehicleId(null);
            await load();
          }}
        />
      )}

      {hasAnything && <PerformanceFilterBar value={activePerfFilter} onChange={setPerfFilter} />}

      {!hasAnything ? (
        <p style={{ color: '#666' }}>No vehicles or investments yet.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                <Th>Fund</Th>
                <Th align="right">Quantity</Th>
                <Th align="right">Price</Th>
                <Th align="right">Book cost</Th>
                <Th align="right">Market value</Th>
                <Th align="right">Gain / Loss</Th>
                <Th align="right">% / yr</Th>
                <Th align="right">Cost / yr</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {sortedVehicles.map((v) => {
                const rows = groups.get(v.id) ?? [];
                return (
                  <VehicleSection
                    key={v.id}
                    vehicle={v}
                    rows={rows}
                    accountId={accountId}
                    isEditing={editingVehicleId === v.id}
                    editForm={editVehicleForm}
                    setEditForm={setEditVehicleForm}
                    submitting={submitting}
                    onStartEdit={() => startEditVehicle(v)}
                    onCancelEdit={() => setEditingVehicleId(null)}
                    onSaveEdit={(e) => onSaveEditVehicle(e, v.id)}
                    onDelete={() => onDeleteVehicle(v)}
                    onImport={() => setImportVehicleId(v.id)}
                    onRecalcBookCosts={() => onRecalcVehicleBookCosts(v)}
                    editingHoldingId={editingHoldingId}
                    editHoldingForm={editHoldingForm}
                    setEditHoldingForm={setEditHoldingForm}
                    funds={funds}
                    vehicles={sortedVehicles}
                    onStartEditHolding={startEditHolding}
                    onCancelEditHolding={() => setEditingHoldingId(null)}
                    onSaveEditHolding={onSaveEditHolding}
                    onDeleteHolding={onDeleteHolding}
                    expandedHoldingId={expandedHoldingId}
                    onToggleExpand={(id) => setExpandedHoldingId((cur) => (cur === id ? null : id))}
                    collapsed={collapsedVehicleIds.has(v.id)}
                    onToggleCollapsed={() =>
                      setCollapsedVehicleIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(v.id)) next.delete(v.id);
                        else next.add(v.id);
                        return next;
                      })
                    }
                  />
                );
              })}
              {unassigned.length > 0 && (
                <UnassignedSection
                  rows={unassigned}
                  accountId={accountId}
                  editingHoldingId={editingHoldingId}
                  editHoldingForm={editHoldingForm}
                  setEditHoldingForm={setEditHoldingForm}
                  submitting={submitting}
                  funds={funds}
                  vehicles={sortedVehicles}
                  onStartEditHolding={startEditHolding}
                  onCancelEditHolding={() => setEditingHoldingId(null)}
                  onSaveEditHolding={onSaveEditHolding}
                  onDeleteHolding={onDeleteHolding}
                  expandedHoldingId={expandedHoldingId}
                  onToggleExpand={(id) => setExpandedHoldingId((cur) => (cur === id ? null : id))}
                  collapsed={unassignedCollapsed}
                  onToggleCollapsed={() => setUnassignedCollapsed((c) => !c)}
                />
              )}
              <tr style={{ fontWeight: 500, background: '#fafafa' }}>
                <Td>Total</Td>
                <Td align="right">—</Td>
                <Td align="right">—</Td>
                <Td align="right">{formatGBP(totalBook)}</Td>
                <Td align="right">{allValued ? formatGBP(totalMarket) : '—'}</Td>
                <Td align="right">
                  {allValued ? (
                    <GainLoss pence={totalGain} pct={totalGainPct ?? undefined} showPct />
                  ) : (
                    '—'
                  )}
                </Td>
                <Td align="right">—</Td>
                <Td align="right">
                  {totalCost > 0 ? (
                    <>
                      {formatGBP(totalCost)}
                      <div style={{ color: '#888', fontSize: '0.78rem' }}>
                        {totalCostPct.toFixed(2)}% all-in
                      </div>
                    </>
                  ) : (
                    '—'
                  )}
                </Td>
                <Td></Td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function VehicleSection({
  vehicle,
  rows,
  accountId,
  isEditing,
  editForm,
  setEditForm,
  submitting,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  onImport,
  onRecalcBookCosts,
  editingHoldingId,
  editHoldingForm,
  setEditHoldingForm,
  funds,
  vehicles,
  onStartEditHolding,
  onCancelEditHolding,
  onSaveEditHolding,
  onDeleteHolding,
  expandedHoldingId,
  onToggleExpand,
  collapsed,
  onToggleCollapsed,
}: {
  vehicle: Vehicle;
  rows: EnrichedHolding[];
  accountId: string;
  isEditing: boolean;
  editForm: VehicleForm;
  setEditForm: (f: VehicleForm) => void;
  submitting: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (e: FormEvent) => void;
  onDelete: () => void;
  onImport: () => void;
  onRecalcBookCosts: () => void;
  editingHoldingId: string | null;
  editHoldingForm: InvestmentForm;
  setEditHoldingForm: (f: InvestmentForm) => void;
  funds: Fund[];
  vehicles: Vehicle[];
  onStartEditHolding: (h: EnrichedHolding) => void;
  onCancelEditHolding: () => void;
  onSaveEditHolding: (e: FormEvent, id: string) => void;
  onDeleteHolding: (id: string) => void;
  expandedHoldingId: string | null;
  onToggleExpand: (id: string) => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}) {
  const investmentsBook = rows.reduce((s, h) => s + h.book_cost_pence, 0);
  const investmentsMarket = rows.reduce(
    (s, h) => (h.market_value_pence != null ? s + h.market_value_pence : s),
    0
  );
  const allValued = rows.every((h) => h.market_value_pence != null);
  const groupBook = investmentsBook + vehicle.cash_pence;
  const groupMarket = investmentsMarket + vehicle.cash_pence;
  const groupGain = groupMarket - groupBook;
  const groupGainPct = allValued && groupBook > 0 ? (groupGain / groupBook) * 100 : null;
  const avgGrowth = weightedAvgGrowth(rows);
  const fundOCFCost = rows.reduce(
    (s, h) =>
      h.market_value_pence != null ? s + (h.market_value_pence * (h.fund_ocf_pct ?? 0)) / 100 : s,
    0
  );
  const platformPctCost = (groupMarket * (vehicle.platform_fee_pct_per_year ?? 0)) / 100;
  const platformFlatCost = vehicle.platform_fee_flat_pence ?? 0;
  const advisorPctCost = (groupMarket * (vehicle.advisor_fee_pct_per_year ?? 0)) / 100;
  const groupCost = Math.round(fundOCFCost + platformPctCost + platformFlatCost + advisorPctCost);
  const groupCostPct = groupMarket > 0 ? (groupCost / groupMarket) * 100 : 0;

  return (
    <Fragment>
      <tr style={{ background: '#eef2ff' }}>
        <td
          colSpan={9}
          style={{
            padding: collapsed ? '0.75rem 0.4rem' : '0.6rem 0.4rem',
            borderBottom: collapsed ? '4px solid #fff' : 'none',
            boxShadow: collapsed ? 'inset 3px 0 0 #818cf8' : 'none',
          }}
        >
          {isEditing ? (
            <VehicleFormUI
              form={editForm}
              setForm={setEditForm}
              submitting={submitting}
              onSubmit={onSaveEdit}
              onCancel={onCancelEdit}
              submitLabel="Save"
              compact
            />
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: '1rem',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
                <CollapseToggle collapsed={collapsed} onToggle={onToggleCollapsed} />
                <div>
                  <strong style={{ fontSize: '1rem' }}>{vehicle.name}</strong>
                  {vehicle.kind && (
                    <span style={{ color: '#666', marginLeft: '0.4rem', fontSize: '0.85rem' }}>
                      · {vehicle.kind}
                    </span>
                  )}
                  {vehicle.owner && (
                    <span style={{ color: '#666', marginLeft: '0.4rem', fontSize: '0.85rem' }}>
                      · {vehicle.owner}
                    </span>
                  )}
                  <div style={{ color: '#555', fontSize: '0.82rem', marginTop: '0.15rem' }}>
                    {rows.length} {rows.length === 1 ? 'investment' : 'investments'}
                    {' · '}
                    {allValued ? formatGBP(groupMarket) : `${formatGBP(groupBook)} book`}
                    {allValued && groupBook > 0 && (
                      <>
                        {' · '}
                        <GainInline pence={groupGain} pct={groupGainPct ?? undefined} />
                      </>
                    )}
                    {avgGrowth != null && (
                      <>
                        {' · avg '}
                        <PctInline pct={avgGrowth} />
                        {' / yr'}
                      </>
                    )}
                    {groupCost > 0 && (
                      <>
                        {' · '}
                        <span
                          title={`${groupCostPct.toFixed(2)}% all-in (fund OCFs + platform fees)`}
                        >
                          {formatGBP(groupCost)}/yr fees
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {!collapsed && (
                <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.85rem' }}>
                  <button onClick={onImport}>Import from paste</button>
                  <button
                    onClick={onRecalcBookCosts}
                    title="Set each holding's book cost to quantity × NAV on its open date"
                  >
                    Recalc book costs
                  </button>
                  <button onClick={onStartEdit}>Edit</button>
                  <button onClick={onDelete}>Delete</button>
                </div>
              )}
            </div>
          )}
        </td>
      </tr>
      {!collapsed && vehicle.cash_pence > 0 && (
        <tr style={{ borderBottom: '1px solid #eee', color: '#444' }}>
          <Td>
            <em>Cash (GBP)</em>
          </Td>
          <Td align="right">—</Td>
          <Td align="right">—</Td>
          <Td align="right">{formatGBP(vehicle.cash_pence)}</Td>
          <Td align="right">{formatGBP(vehicle.cash_pence)}</Td>
          <Td align="right">—</Td>
          <Td align="right">—</Td>
          <Td align="right">—</Td>
          <Td></Td>
        </tr>
      )}
      {!collapsed &&
        rows.map((h) => {
          const isEditingH = editingHoldingId === h.id;
          const expanded = expandedHoldingId === h.id;
          return (
            <Fragment key={h.id}>
              <tr style={{ borderBottom: '1px solid #eee', verticalAlign: 'top' }}>
                {isEditingH ? (
                  <td colSpan={9} style={{ padding: '0.75rem 0.25rem' }}>
                    <EditInvestmentForm
                      h={h}
                      form={editHoldingForm}
                      setForm={setEditHoldingForm}
                      funds={funds}
                      vehicles={vehicles}
                      submitting={submitting}
                      onSubmit={(e) => onSaveEditHolding(e, h.id)}
                      onCancel={onCancelEditHolding}
                    />
                  </td>
                ) : (
                  <HoldingRow
                    h={h}
                    accountId={accountId}
                    expanded={expanded}
                    onToggleExpand={() => onToggleExpand(h.id)}
                    onEdit={() => onStartEditHolding(h)}
                    onDelete={() => onDeleteHolding(h.id)}
                  />
                )}
              </tr>
              {expanded && !isEditingH && (
                <ChartDrawerRow accountId={accountId} fundId={h.fund_id} openedAt={h.opened_at} />
              )}
            </Fragment>
          );
        })}
      {!collapsed && (rows.length > 0 || vehicle.cash_pence > 0) && (
        <tr style={{ background: '#fafafa', fontSize: '0.85rem', color: '#555' }}>
          <Td>
            <em>Subtotal</em>
          </Td>
          <Td align="right">—</Td>
          <Td align="right">—</Td>
          <Td align="right">{formatGBP(groupBook)}</Td>
          <Td align="right">{allValued ? formatGBP(groupMarket) : '—'}</Td>
          <Td align="right">
            {allValued ? (
              <GainLoss pence={groupGain} pct={groupGainPct ?? undefined} showPct />
            ) : (
              '—'
            )}
          </Td>
          <Td align="right">{avgGrowth != null ? <PctInline pct={avgGrowth} /> : '—'}</Td>
          <Td align="right">
            {groupCost > 0 ? (
              <>
                {formatGBP(groupCost)}
                <div style={{ color: '#888', fontSize: '0.78rem' }}>
                  {groupCostPct.toFixed(2)}% all-in
                </div>
              </>
            ) : (
              '—'
            )}
          </Td>
          <Td></Td>
        </tr>
      )}
    </Fragment>
  );
}

function UnassignedSection({
  rows,
  accountId,
  editingHoldingId,
  editHoldingForm,
  setEditHoldingForm,
  submitting,
  funds,
  vehicles,
  onStartEditHolding,
  onCancelEditHolding,
  onSaveEditHolding,
  onDeleteHolding,
  expandedHoldingId,
  onToggleExpand,
  collapsed,
  onToggleCollapsed,
}: {
  rows: EnrichedHolding[];
  accountId: string;
  editingHoldingId: string | null;
  editHoldingForm: InvestmentForm;
  setEditHoldingForm: (f: InvestmentForm) => void;
  submitting: boolean;
  funds: Fund[];
  vehicles: Vehicle[];
  onStartEditHolding: (h: EnrichedHolding) => void;
  onCancelEditHolding: () => void;
  onSaveEditHolding: (e: FormEvent, id: string) => void;
  onDeleteHolding: (id: string) => void;
  expandedHoldingId: string | null;
  onToggleExpand: (id: string) => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}) {
  const groupBook = rows.reduce((s, h) => s + h.book_cost_pence, 0);
  const groupMarket = rows.reduce(
    (s, h) => (h.market_value_pence != null ? s + h.market_value_pence : s),
    0
  );
  const allValued = rows.every((h) => h.market_value_pence != null);
  const groupGain = groupMarket - groupBook;
  const groupGainPct = allValued && groupBook > 0 ? (groupGain / groupBook) * 100 : null;
  const avgGrowth = weightedAvgGrowth(rows);
  return (
    <Fragment>
      <tr style={{ background: '#f3f4f6' }}>
        <td colSpan={9} style={{ padding: '0.5rem 0.4rem', color: '#666', fontSize: '0.85rem' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
            <CollapseToggle collapsed={collapsed} onToggle={onToggleCollapsed} />
            <div>
              <em>Unassigned (no vehicle — assign one to clear this section)</em>
              <div style={{ color: '#555', fontSize: '0.82rem', marginTop: '0.15rem' }}>
                {rows.length} {rows.length === 1 ? 'investment' : 'investments'}
                {' · '}
                {allValued ? formatGBP(groupMarket) : `${formatGBP(groupBook)} book`}
                {avgGrowth != null && (
                  <>
                    {' · avg '}
                    <PctInline pct={avgGrowth} />
                    {' / yr'}
                  </>
                )}
              </div>
            </div>
          </div>
        </td>
      </tr>
      {!collapsed &&
        rows.map((h) => {
          const isEditingH = editingHoldingId === h.id;
          const expanded = expandedHoldingId === h.id;
          return (
            <Fragment key={h.id}>
              <tr style={{ borderBottom: '1px solid #eee', verticalAlign: 'top' }}>
                {isEditingH ? (
                  <td colSpan={9} style={{ padding: '0.75rem 0.25rem' }}>
                    <EditInvestmentForm
                      h={h}
                      form={editHoldingForm}
                      setForm={setEditHoldingForm}
                      funds={funds}
                      vehicles={vehicles}
                      submitting={submitting}
                      onSubmit={(e) => onSaveEditHolding(e, h.id)}
                      onCancel={onCancelEditHolding}
                    />
                  </td>
                ) : (
                  <HoldingRow
                    h={h}
                    accountId={accountId}
                    expanded={expanded}
                    onToggleExpand={() => onToggleExpand(h.id)}
                    onEdit={() => onStartEditHolding(h)}
                    onDelete={() => onDeleteHolding(h.id)}
                  />
                )}
              </tr>
              {expanded && !isEditingH && (
                <ChartDrawerRow accountId={accountId} fundId={h.fund_id} openedAt={h.opened_at} />
              )}
            </Fragment>
          );
        })}
      {!collapsed && rows.length > 1 && (
        <tr style={{ background: '#fafafa', fontSize: '0.85rem', color: '#555' }}>
          <Td>
            <em>Subtotal</em>
          </Td>
          <Td align="right">—</Td>
          <Td align="right">—</Td>
          <Td align="right">{formatGBP(groupBook)}</Td>
          <Td align="right">{allValued ? formatGBP(groupMarket) : '—'}</Td>
          <Td align="right">
            {allValued ? (
              <GainLoss pence={groupGain} pct={groupGainPct ?? undefined} showPct />
            ) : (
              '—'
            )}
          </Td>
          <Td align="right">{avgGrowth != null ? <PctInline pct={avgGrowth} /> : '—'}</Td>
          <Td align="right">—</Td>
          <Td></Td>
        </tr>
      )}
    </Fragment>
  );
}

function HoldingRow({
  h,
  accountId,
  expanded,
  onToggleExpand,
  onEdit,
  onDelete,
}: {
  h: EnrichedHolding;
  accountId: string;
  expanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const gainPct =
    h.market_value_pence != null && h.book_cost_pence > 0
      ? ((h.market_value_pence - h.book_cost_pence) / h.book_cost_pence) * 100
      : null;
  return (
    <>
      <Td>
        <button
          onClick={onToggleExpand}
          aria-label={expanded ? 'Hide chart' : 'Show chart'}
          title={expanded ? 'Hide price chart' : 'Show price chart'}
          style={{
            border: '1px solid #d4d4d8',
            background: expanded ? '#eef2ff' : '#fff',
            cursor: 'pointer',
            color: '#3b82f6',
            padding: '0 0.35rem',
            marginRight: '0.5rem',
            fontSize: '0.95rem',
            lineHeight: 1.1,
            borderRadius: 4,
            display: 'inline-block',
            verticalAlign: 'middle',
          }}
        >
          {expanded ? '▾' : '▸'}
        </button>
        <Link
          to={`/accounts/${encodeURIComponent(accountId)}/funds/${encodeURIComponent(h.fund_id)}`}
          style={{ color: 'inherit', fontWeight: 500 }}
        >
          {h.fund_name || h.fund_id}
        </Link>
        {(h.provider || h.platform || h.opened_at) && (
          <div style={{ color: '#888', fontSize: '0.82rem', marginTop: '0.15rem' }}>
            {h.provider}
            {h.provider && h.platform ? ' · ' : ''}
            {h.platform}
            {(h.provider || h.platform) && h.opened_at ? ' · ' : ''}
            {h.opened_at && `since ${h.opened_at}`}
          </div>
        )}
      </Td>
      <Td align="right">{formatQty(h.quantity)}</Td>
      <Td align="right">
        {h.current_price_pence != null ? (
          <>
            {formatPrice(h.current_price_pence, h.price_currency)}
            {h.price_as_of && (
              <div style={{ color: '#888', fontSize: '0.78rem' }}>{h.price_as_of}</div>
            )}
          </>
        ) : (
          <span style={{ color: '#888' }}>—</span>
        )}
      </Td>
      <Td align="right">{formatGBP(h.book_cost_pence)}</Td>
      <Td align="right">
        {h.market_value_pence != null ? (
          formatGBP(h.market_value_pence)
        ) : (
          <span style={{ color: '#888' }}>—</span>
        )}
      </Td>
      <Td align="right">
        {h.gain_loss_pence != null ? (
          <GainLoss pence={h.gain_loss_pence} pct={gainPct ?? undefined} showPct />
        ) : (
          <span style={{ color: '#888' }}>—</span>
        )}
      </Td>
      <Td align="right">
        {h.growth_pct_per_year != null ? (
          <PctPerYear pct={h.growth_pct_per_year} asOf={h.nav_at_open_as_of} />
        ) : (
          <span style={{ color: '#888' }}>—</span>
        )}
      </Td>
      <Td align="right">
        {h.market_value_pence != null && (h.fund_ocf_pct ?? 0) > 0 ? (
          <>
            {formatGBP(Math.round((h.market_value_pence * (h.fund_ocf_pct ?? 0)) / 100))}
            <div style={{ color: '#888', fontSize: '0.78rem' }}>{h.fund_ocf_pct ?? 0}% OCF</div>
          </>
        ) : (
          <span style={{ color: '#888' }}>—</span>
        )}
      </Td>
      <Td align="right" nowrap>
        <button onClick={onEdit}>Edit</button> <button onClick={onDelete}>Delete</button>
      </Td>
    </>
  );
}

function PctPerYear({ pct, asOf }: { pct: number; asOf?: string }) {
  const positive = pct >= 0;
  const colour = pct === 0 ? '#444' : positive ? '#0a7d32' : '#b00';
  const sign = pct > 0 ? '+' : '';
  return (
    <span style={{ color: colour }}>
      {sign}
      {pct.toFixed(2)}%
      {asOf && <div style={{ color: '#888', fontSize: '0.78rem' }}>since {asOf}</div>}
    </span>
  );
}

// Inline coloured % used in vehicle summary rows. Same colour rule as
// PctPerYear but no "since" line and no block layout.
function PctInline({ pct }: { pct: number }) {
  const positive = pct >= 0;
  const colour = pct === 0 ? '#444' : positive ? '#0a7d32' : '#b00';
  const sign = pct > 0 ? '+' : '';
  return (
    <span style={{ color: colour, fontWeight: 500 }}>
      {sign}
      {pct.toFixed(2)}%
    </span>
  );
}

// Inline coloured "+£X.XX (+Y.YY%)" used in vehicle summary rows. The
// table-cell GainLoss component breaks pct onto its own line, which
// doesn't fit a one-line header — this keeps it inline.
function GainInline({ pence, pct }: { pence: number; pct?: number }) {
  const colour = pence === 0 ? '#444' : pence > 0 ? '#0a7d32' : '#b00';
  const sign = pence > 0 ? '+' : '';
  return (
    <span style={{ color: colour, fontWeight: 500 }}>
      {sign}
      {formatGBP(pence)}
      {pct != null && ` (${pence >= 0 ? '+' : ''}${pct.toFixed(2)}%)`}
    </span>
  );
}

function CollapseToggle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      aria-label={collapsed ? 'Expand vehicle' : 'Collapse vehicle'}
      title={collapsed ? 'Expand' : 'Collapse'}
      style={{
        border: '1px solid #c7d2fe',
        background: '#fff',
        cursor: 'pointer',
        color: '#3b82f6',
        padding: '0 0.4rem',
        fontSize: '0.95rem',
        lineHeight: 1.1,
        borderRadius: 4,
      }}
    >
      {collapsed ? '▸' : '▾'}
    </button>
  );
}

function PerformanceFilterBar({
  value,
  onChange,
}: {
  value: PerformanceFilter;
  onChange: (v: PerformanceFilter) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '0.4rem',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        margin: '0 0 0.75rem',
        fontSize: '0.85rem',
      }}
    >
      <span style={{ color: '#666' }}>Filter by performance:</span>
      {PERF_FILTERS.map((f) => {
        const active = value === f.key;
        return (
          <button
            key={f.key}
            onClick={() => onChange(active ? null : f.key)}
            style={{
              padding: '0.2rem 0.6rem',
              fontSize: '0.82rem',
              background: active ? f.colour : '#f4f4f5',
              color: active ? '#fff' : f.colour,
              border: '1px solid',
              borderColor: active ? f.colour : '#ddd',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            {f.label}
          </button>
        );
      })}
      {value != null && (
        <button
          onClick={() => onChange(null)}
          style={{
            padding: '0.2rem 0.6rem',
            fontSize: '0.82rem',
            background: '#fff',
            color: '#555',
            border: '1px solid #ddd',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          Clear
        </button>
      )}
    </div>
  );
}

// ChartDrawerRow lazy-loads the per-fund price history when its parent
// row is expanded. Cached at the API/CDN layer, but we still defer the
// fetch until the drawer is opened so collapsed rows stay cheap.
function ChartDrawerRow({
  accountId,
  fundId,
  openedAt,
}: {
  accountId: string;
  fundId: string;
  openedAt?: string;
}) {
  const [prices, setPrices] = useState<FundPriceSnapshot[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    listFundPrices(accountId, fundId)
      .then((res) => {
        if (!cancelled) setPrices(res.prices);
      })
      .catch((e) => {
        if (!cancelled) setErr(asMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, fundId]);
  return (
    <tr style={{ background: '#fafafa' }}>
      <td colSpan={9} style={{ padding: '0.75rem 0.5rem' }}>
        {err ? (
          <p style={{ color: '#b00', margin: 0 }}>{err}</p>
        ) : prices == null ? (
          <p style={{ color: '#888', margin: 0 }}>Loading chart…</p>
        ) : (
          <FundPriceChart prices={prices} markerDate={openedAt} markerLabel="Opened" />
        )}
      </td>
    </tr>
  );
}

function VehicleFormUI({
  form,
  setForm,
  submitting,
  onSubmit,
  onCancel,
  submitLabel,
  compact,
}: {
  form: VehicleForm;
  setForm: (f: VehicleForm) => void;
  submitting: boolean;
  onSubmit: (e: FormEvent) => void;
  onCancel?: () => void;
  submitLabel: string;
  compact?: boolean;
}) {
  return (
    <form
      onSubmit={onSubmit}
      style={{
        border: compact ? 'none' : '1px solid #ddd',
        borderRadius: 6,
        padding: compact ? '0' : '1rem',
        marginBottom: compact ? 0 : '1.5rem',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr',
        gap: '0.5rem 1rem',
      }}
    >
      <Field label="Name">
        <input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. Andy SIPP"
          required
        />
      </Field>
      <Field label="Owner">
        <input
          value={form.owner}
          onChange={(e) => setForm({ ...form, owner: e.target.value })}
          placeholder="e.g. Andy"
          required
        />
      </Field>
      <Field label="Kind">
        <input
          list="vehicle-kind-options"
          value={form.kind}
          onChange={(e) => setForm({ ...form, kind: e.target.value })}
          placeholder="e.g. SIPP"
        />
        <datalist id="vehicle-kind-options">
          {KIND_SUGGESTIONS.map((k) => (
            <option key={k} value={k} />
          ))}
        </datalist>
      </Field>
      <Field label="Cash (£)">
        <input
          type="number"
          step="0.01"
          min="0"
          value={form.cash_pounds}
          onChange={(e) => setForm({ ...form, cash_pounds: e.target.value })}
          placeholder="0.00"
        />
      </Field>
      <Field label="Platform fee % / yr">
        <input
          type="number"
          step="0.01"
          min="0"
          value={form.platform_fee_pct}
          onChange={(e) => setForm({ ...form, platform_fee_pct: e.target.value })}
          placeholder="0.20"
        />
      </Field>
      <Field label="Platform flat £ / yr">
        <input
          type="number"
          step="0.01"
          min="0"
          value={form.platform_fee_flat_pounds}
          onChange={(e) => setForm({ ...form, platform_fee_flat_pounds: e.target.value })}
          placeholder="155.88"
        />
      </Field>
      <Field label="Advisor fee % / yr">
        <input
          type="number"
          step="0.01"
          min="0"
          value={form.advisor_fee_pct}
          onChange={(e) => setForm({ ...form, advisor_fee_pct: e.target.value })}
          placeholder="0.75"
        />
      </Field>
      <div
        style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}
      >
        {onCancel && (
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

function InvestmentFormUI({
  form,
  setForm,
  funds,
  vehicles,
  submitting,
  onSubmit,
  onCancel,
  submitLabel,
}: {
  form: InvestmentForm;
  setForm: (f: InvestmentForm) => void;
  funds: Fund[];
  vehicles: Vehicle[];
  submitting: boolean;
  onSubmit: (e: FormEvent) => void;
  onCancel?: () => void;
  submitLabel: string;
}) {
  return (
    <form
      onSubmit={onSubmit}
      style={{
        border: '1px solid #ddd',
        borderRadius: 6,
        padding: '1rem',
        marginBottom: '1.5rem',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.5rem 1rem',
      }}
    >
      <Field label="Fund">
        <select
          value={form.fund_id}
          onChange={(e) => setForm({ ...form, fund_id: e.target.value })}
          required
          style={{ width: '100%' }}
        >
          <option value="">— Select a fund —</option>
          {funds.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name || f.id}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Vehicle">
        <select
          value={form.vehicle_id}
          onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}
          required
          style={{ width: '100%' }}
        >
          <option value="">— Select a vehicle —</option>
          {vehicles.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
              {v.kind ? ` · ${v.kind}` : ''}
              {v.owner ? ` · ${v.owner}` : ''}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Quantity (units)">
        <input
          type="number"
          step="any"
          min="0"
          value={form.quantity}
          onChange={(e) => setForm({ ...form, quantity: e.target.value })}
          required
        />
      </Field>
      <Field label="Book cost (£)">
        <input
          type="number"
          step="0.01"
          min="0"
          value={form.book_cost_pounds}
          onChange={(e) => setForm({ ...form, book_cost_pounds: e.target.value })}
          required
        />
      </Field>
      <Field label="Provider (optional)">
        <input
          value={form.provider}
          onChange={(e) => setForm({ ...form, provider: e.target.value })}
          placeholder="e.g. Vanguard"
        />
      </Field>
      <Field label="Platform (optional)">
        <input
          value={form.platform}
          onChange={(e) => setForm({ ...form, platform: e.target.value })}
          placeholder="e.g. ii"
        />
      </Field>
      <Field label="Start date">
        <input
          type="date"
          value={form.opened_at}
          onChange={(e) => setForm({ ...form, opened_at: e.target.value })}
        />
      </Field>
      <div
        style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}
      >
        {onCancel && (
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

function EditInvestmentForm({
  h,
  form,
  setForm,
  funds,
  vehicles,
  submitting,
  onSubmit,
  onCancel,
}: {
  h: EnrichedHolding;
  form: InvestmentForm;
  setForm: (f: InvestmentForm) => void;
  funds: Fund[];
  vehicles: Vehicle[];
  submitting: boolean;
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
}) {
  // Fund stays fixed on edit (changing it would orphan the GSI fan-out target).
  void funds;
  return (
    <form
      onSubmit={onSubmit}
      style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1rem' }}
    >
      <div style={{ gridColumn: '1 / -1', color: '#666' }}>
        <strong>{h.fund_name || h.fund_id}</strong>
        <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem' }}>
          (fund cannot be changed — delete and re-add to switch)
        </span>
      </div>
      <Field label="Vehicle">
        <select
          value={form.vehicle_id}
          onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}
          required
          style={{ width: '100%' }}
        >
          <option value="">— Select a vehicle —</option>
          {vehicles.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
              {v.kind ? ` · ${v.kind}` : ''}
              {v.owner ? ` · ${v.owner}` : ''}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Quantity (units)">
        <input
          type="number"
          step="any"
          min="0"
          value={form.quantity}
          onChange={(e) => setForm({ ...form, quantity: e.target.value })}
          required
        />
      </Field>
      <Field label="Book cost (£)">
        <input
          type="number"
          step="0.01"
          min="0"
          value={form.book_cost_pounds}
          onChange={(e) => setForm({ ...form, book_cost_pounds: e.target.value })}
          required
        />
      </Field>
      <Field label="Provider">
        <input
          value={form.provider}
          onChange={(e) => setForm({ ...form, provider: e.target.value })}
        />
      </Field>
      <Field label="Platform">
        <input
          value={form.platform}
          onChange={(e) => setForm({ ...form, platform: e.target.value })}
        />
      </Field>
      <Field label="Start date">
        <input
          type="date"
          value={form.opened_at}
          onChange={(e) => setForm({ ...form, opened_at: e.target.value })}
        />
      </Field>
      <div
        style={{
          gridColumn: '1 / -1',
          display: 'flex',
          gap: '0.5rem',
          justifyContent: 'flex-end',
        }}
      >
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

// ImportFromPaste handles the two-step paste import: textarea → preview
// (Morningstar candidates per row) → confirm (writes funds + holdings).
// Each row's selected ISIN drives what gets written; "skip" leaves the row out.
function ImportFromPaste({
  accountId,
  vehicle,
  onClose,
  onImported,
}: {
  accountId: string;
  vehicle: Vehicle;
  onClose: () => void;
  onImported: () => Promise<void> | void;
}) {
  const [paste, setPaste] = useState('');
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  // selections[rowIndex] = ISIN of chosen match, or '' to skip.
  const [selections, setSelections] = useState<string[]>([]);
  // openedAt anchors book cost to historical NAV at the chosen date.
  // Default to today (same-day buy = book == MV); past date back-dates
  // the cost basis so gain/loss reflects price movement since then.
  const [openedAt, setOpenedAt] = useState<string>(todayISO());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResponse | null>(null);

  async function onPreview(e: FormEvent) {
    e.preventDefault();
    if (!paste.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const p = await previewVehicleImport(accountId, vehicle.id, paste);
      setPreview(p);
      setSelections(p.rows.map((r) => (r.matches.length > 0 ? r.matches[0].isin || '' : '')));
    } catch (e) {
      setErr(asMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function onConfirm() {
    if (!preview) return;
    const items: { isin: string; market_value_pence: number }[] = [];
    preview.rows.forEach((r, i) => {
      const isin = selections[i];
      if (isin) items.push({ isin, market_value_pence: r.market_value_pence });
    });
    if (items.length === 0) {
      setErr('Pick at least one match to import');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const r = await confirmVehicleImport(accountId, vehicle.id, items, openedAt || undefined);
      setResult(r);
    } catch (e) {
      setErr(asMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function onDone() {
    await onImported();
  }

  return (
    <div
      style={{
        border: '1px solid #c7d2fe',
        background: '#f5f7ff',
        borderRadius: 8,
        padding: '1rem',
        marginBottom: '1.5rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: '0.75rem',
        }}
      >
        <strong>Import investments into {vehicle.name}</strong>
        <button onClick={onClose}>Close</button>
      </div>
      {err && <p style={{ color: '#b00', margin: '0 0 0.5rem' }}>{err}</p>}

      {result ? (
        <ImportResultView result={result} onDone={onDone} />
      ) : preview ? (
        <PreviewStep
          preview={preview}
          selections={selections}
          setSelection={(i, isin) => {
            const next = [...selections];
            next[i] = isin;
            setSelections(next);
          }}
          openedAt={openedAt}
          setOpenedAt={setOpenedAt}
          submitting={busy}
          onConfirm={onConfirm}
          onBack={() => setPreview(null)}
        />
      ) : (
        <PasteStep paste={paste} setPaste={setPaste} submitting={busy} onSubmit={onPreview} />
      )}
    </div>
  );
}

function PasteStep({
  paste,
  setPaste,
  submitting,
  onSubmit,
}: {
  paste: string;
  setPaste: (s: string) => void;
  submitting: boolean;
  onSubmit: (e: FormEvent) => void;
}) {
  return (
    <form onSubmit={onSubmit}>
      <p style={{ margin: '0 0 0.5rem', color: '#555', fontSize: '0.9rem' }}>
        Paste the "Investment Detail" panel from interactive investor (six lines per row: name,
        type, value, %, asset class, sector). Re-pasting later updates quantities and adds new funds
        — existing book costs are preserved.
      </p>
      <textarea
        value={paste}
        onChange={(e) => setPaste(e.target.value)}
        rows={10}
        style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.85rem' }}
        placeholder={
          'abrdn American Equity Tracker N Acc\nAccumulation Fund\n£4,219.23\n14.25%\nEquity\nNorth America\n…'
        }
        required
      />
      <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
        <button type="submit" disabled={submitting}>
          {submitting ? 'Looking up…' : 'Preview matches'}
        </button>
      </div>
    </form>
  );
}

function PreviewStep({
  preview,
  selections,
  setSelection,
  openedAt,
  setOpenedAt,
  submitting,
  onConfirm,
  onBack,
}: {
  preview: ImportPreviewResponse;
  selections: string[];
  setSelection: (i: number, isin: string) => void;
  openedAt: string;
  setOpenedAt: (s: string) => void;
  submitting: boolean;
  onConfirm: () => void;
  onBack: () => void;
}) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: '0.75rem',
          margin: '0 0 0.5rem',
          fontSize: '0.85rem',
          color: '#444',
        }}
      >
        <label>
          Bought on:{' '}
          <input
            type="date"
            value={openedAt}
            onChange={(e) => setOpenedAt(e.target.value)}
            max={todayISO()}
          />
        </label>
        <span style={{ color: '#888' }}>
          Book cost is anchored to the NAV on this date — leave as today for a same-day buy-in.
        </span>
      </div>
      <p style={{ margin: '0 0 0.5rem', color: '#555', fontSize: '0.9rem' }}>
        Confirm the Morningstar match for each row. Pick "Skip" to leave a row out.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: '1px solid #ccc' }}>
            <Th>Pasted name</Th>
            <Th align="right">Market value</Th>
            <Th>Match</Th>
          </tr>
        </thead>
        <tbody>
          {preview.rows.map((r, i) => (
            <PreviewRow
              key={i}
              row={r}
              selected={selections[i] ?? ''}
              onChange={(isin) => setSelection(i, isin)}
            />
          ))}
        </tbody>
      </table>
      <div
        style={{
          marginTop: '0.75rem',
          display: 'flex',
          gap: '0.5rem',
          justifyContent: 'flex-end',
        }}
      >
        <button type="button" onClick={onBack}>
          Back
        </button>
        <button type="button" onClick={onConfirm} disabled={submitting}>
          {submitting ? 'Importing…' : 'Import selected'}
        </button>
      </div>
    </div>
  );
}

function PreviewRow({
  row,
  selected,
  onChange,
}: {
  row: ImportPreviewRow;
  selected: string;
  onChange: (isin: string) => void;
}) {
  return (
    <tr style={{ borderBottom: '1px solid #eee', verticalAlign: 'top' }}>
      <Td>
        <div style={{ fontWeight: 500 }}>{row.name}</div>
        {row.type && (
          <div style={{ color: '#888', fontSize: '0.8rem' }}>
            {row.type}
            {row.asset_class ? ` · ${row.asset_class}` : ''}
            {row.sector ? ` · ${row.sector}` : ''}
          </div>
        )}
      </Td>
      <Td align="right">{formatGBP(row.market_value_pence)}</Td>
      <Td>
        {row.matches.length === 0 ? (
          <span style={{ color: '#888' }}>No matches — will be skipped</span>
        ) : (
          <select
            value={selected}
            onChange={(e) => onChange(e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="">— Skip this row —</option>
            {row.matches.map((m) => (
              <option key={m.sec_id} value={m.isin || ''} disabled={!m.isin}>
                {m.name}
                {m.isin ? ` · ${m.isin}` : ' (no ISIN)'}
                {m.category ? ` · ${m.category}` : ''}
              </option>
            ))}
          </select>
        )}
      </Td>
    </tr>
  );
}

function ImportResultView({ result, onDone }: { result: ImportResponse; onDone: () => void }) {
  return (
    <div>
      <p style={{ margin: '0 0 0.5rem' }}>
        <strong>{result.created}</strong> created, <strong>{result.updated}</strong> updated,{' '}
        <strong>{result.skipped}</strong> skipped.
      </p>
      <ul style={{ margin: '0 0 0.75rem', paddingLeft: '1.2rem', fontSize: '0.85rem' }}>
        {result.results.map((r, i) => (
          <li key={i}>
            <code>{r.isin || '—'}</code> · {r.status}
            {r.reason ? ` (${r.reason})` : ''}
            {r.quantity != null ? ` · ${formatQty(r.quantity)} units` : ''}
          </li>
        ))}
      </ul>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={onDone}>Done</button>
      </div>
    </div>
  );
}

function GainLoss({ pence, pct, showPct }: { pence: number; pct?: number; showPct?: boolean }) {
  const positive = pence >= 0;
  const colour = pence === 0 ? '#444' : positive ? '#0a7d32' : '#b00';
  const sign = pence > 0 ? '+' : '';
  return (
    <span style={{ color: colour }}>
      {sign}
      {formatGBP(pence)}
      {showPct && pct != null && (
        <div style={{ fontSize: '0.78rem' }}>
          {pence >= 0 ? '+' : ''}
          {pct.toFixed(2)}%
        </div>
      )}
    </span>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem', color: '#444' }}>
      <span style={{ marginBottom: '0.2rem' }}>{label}</span>
      {children}
    </label>
  );
}

function Th({
  children,
  align,
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      style={{
        padding: '0.5rem 0.4rem',
        textAlign: align ?? 'left',
        fontWeight: 500,
        color: '#555',
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  nowrap,
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  nowrap?: boolean;
}) {
  return (
    <td
      style={{
        padding: '0.6rem 0.4rem',
        textAlign: align ?? 'left',
        whiteSpace: nowrap ? 'nowrap' : undefined,
      }}
    >
      {children}
    </td>
  );
}

const gbp = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
});

// gbpPrice keeps NAV precision (II shows 4 dp like 291.3548) — separate from
// gbp which is used for monetary totals where 2 dp is right.
const gbpPrice = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

function formatGBP(pence: number): string {
  return gbp.format(pence / 100);
}

function formatQty(q: number): string {
  return q.toLocaleString('en-GB', { maximumFractionDigits: 8 });
}

// formatPrice mirrors II's display: GBX-quoted funds (single-priced unit
// trusts like Jupiter India) keep their pence form ("232.8140p") regardless
// of magnitude; GBP-quoted ones render as pounds ("£291.3548"). When the
// currency isn't known, fall back to the £1 threshold heuristic. Keeps up
// to four decimal places so unit-trust prices round-trip without truncation.
function formatPrice(pence: number, currency?: string): string {
  if (currency === 'GBX') {
    return `${pence.toLocaleString('en-GB', { maximumFractionDigits: 4 })}p`;
  }
  if (currency === 'GBP') {
    return gbpPrice.format(pence / 100);
  }
  if (pence < 100) {
    return `${pence.toLocaleString('en-GB', { maximumFractionDigits: 4 })}p`;
  }
  return gbpPrice.format(pence / 100);
}

function asMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}
